import { zodResolver } from '@hookform/resolvers/zod';
import { forgotPasswordSchema, okSchema } from '@hearth/shared';
import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Loader2, MailCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/api';
import { applyServerErrors } from '@/lib/forms';

export const Route = createFileRoute('/_guest/forgot-password')({ component: ForgotPasswordPage });

type Values = z.input<typeof forgotPasswordSchema>;

function ForgotPasswordPage() {
  const form = useForm<Values>({ resolver: zodResolver(forgotPasswordSchema) });
  const request = useMutation({
    mutationFn: (body: Values) =>
      apiRequest('/api/auth/password/forgot', { method: 'POST', body, schema: okSchema }),
    onError: (err) => applyServerErrors(err, form.setError),
  });

  if (request.isSuccess) {
    return (
      <Card>
        <CardHeader>
          <MailCheck className="text-primary mb-2 size-8" />
          <CardTitle>Check your email</CardTitle>
          <CardDescription>
            If an account exists for {form.getValues('email')}, we&apos;ve sent a link to reset its
            password. The link expires in 60 minutes.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login" search={{ redirect: undefined }}>
              Back to sign in
            </Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reset your password</CardTitle>
        <CardDescription>
          Enter your account&apos;s email and we&apos;ll send you a reset link.
        </CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => request.mutate(v))} noValidate>
        <CardContent>
          <FormField label="Email" error={form.formState.errors.email?.message}>
            <Input type="email" autoComplete="email" autoFocus {...form.register('email')} />
          </FormField>
        </CardContent>
        <CardFooter className="mt-6 flex-col gap-3">
          <Button type="submit" className="w-full" disabled={request.isPending}>
            {request.isPending && <Loader2 className="animate-spin" />} Send reset link
          </Button>
          <Link
            to="/login"
            search={{ redirect: undefined }}
            className="text-muted-foreground hover:text-foreground text-sm"
          >
            Back to sign in
          </Link>
        </CardFooter>
      </form>
    </Card>
  );
}
